import { tracked } from '@glimmer/tracking';

export default class CodeInputManager {
  @tracked values = [];
  @tracked showError = false;

  constructor(digitCount = 6) {
    this.digitCount = digitCount;
    this.values = new Array(digitCount).fill('');
    this.isNavigatingWithArrows = false;
    this.hasAttemptedSubmission = false; // Track if user has already attempted submission
  }

  /**
   * Handle keydown events for arrow key navigation
   * @param {number} index - The index of the current input field
   * @param {Event} event - The keydown event
   */
  handleKeyDown(index, event) {
    // Handle arrow key navigation
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();

      // Set flag to prevent auto-advance during arrow navigation
      this.isNavigatingWithArrows = true;

      const inputs = this.getInputElements(event.target);
      if (!inputs || inputs.length === 0) {
        return;
      }

      let targetIndex;
      if (event.key === 'ArrowLeft') {
        // Move to previous input
        targetIndex = Math.max(0, index - 1);
      } else {
        // Move to next input
        targetIndex = Math.min(this.digitCount - 1, index + 1);
      }

      if (inputs[targetIndex]) {
        // Use a small delay to ensure the current event is fully processed
        setTimeout(() => {
          inputs[targetIndex].focus();
          this.selectInputContent(inputs[targetIndex]);
          // Reset flag after navigation is complete
          setTimeout(() => {
            this.isNavigatingWithArrows = false;
          }, 200);
        }, 0);
      } else {
        // Fallback: try to find inputs by simple DOM order
        this.fallbackArrowNavigation(event.target, event.key === 'ArrowLeft' ? -1 : 1);
        // Reset flag after fallback navigation
        setTimeout(() => {
          this.isNavigatingWithArrows = false;
        }, 200);
      }
    }
  }

  /**
   * Fallback arrow navigation using simple DOM traversal
   * @param {HTMLElement} currentInput - The current input element
   * @param {number} direction - Direction to navigate (-1 for left, 1 for right)
   */
  fallbackArrowNavigation(currentInput, direction) {
    if (!currentInput || !currentInput.parentElement) {
      return;
    }

    // Get all inputs in DOM order
    const allInputs = Array.from(currentInput.parentElement.querySelectorAll('input[type="text"], input[type="number"]'));
    const currentIndex = allInputs.indexOf(currentInput);

    if (currentIndex === -1) {
      return;
    }

    const targetIndex = currentIndex + direction;

    if (targetIndex >= 0 && targetIndex < allInputs.length) {
      allInputs[targetIndex].focus();
      this.selectInputContent(allInputs[targetIndex]);
    }
  }

  /**
   * Handle focus events to select input content
   * @param {Event} event - The focus event
   */
  handleFocus(event) {
    // Select content when input receives focus
    const input = event.target;
    if (input) {
      // Use a small delay to ensure focus is complete
      setTimeout(() => {
        this.selectInputContent(input);
      }, 0);
    }
  }

  /**
   * Select all content in an input field
   * @param {HTMLElement} input - The input element to select
   */
  selectInputContent(input) {
    if (input && input.value) {
      // Only select if there's content to select
      try {
        // Method 1: Use select() method
        if (input.select) {
          input.select();
        }

        // Method 2: Use setSelectionRange for more control
        if (input.setSelectionRange) {
          const length = input.value.length;
          input.setSelectionRange(0, length);
        }
      } catch (error) {
        // Silently handle selection errors
      }
    }
  }

  /**
   * Handle keyup events for code input fields
   * @param {number} index - The index of the current input field
   * @param {Event} event - The keyup event
   * @param {Function} onComplete - Callback when all digits are filled
   */
  handleKeyUp(index, event, onComplete) {
    this.showError = false;

    const input = event.target;
    let value = input.value;

    // Only allow digits
    if (!/^\d*$/.test(value)) {
      value = value.replace(/\D/g, '');
      input.value = value;
    }

    // Limit to single digit
    if (value.length > 1) {
      value = value.slice(0, 1);
      input.value = value;
    }

    // Update the value in our tracked array
    this.values[index] = value;

    // Skip auto-advance if we're navigating with arrows
    if (this.isNavigatingWithArrows) {
      return;
    }

    // Handle navigation
    if (value.length === 1) {
      // Move to next input if there is one
      if (index < this.digitCount - 1) {
        const nextInput = input.parentElement.children[index + 1];
        if (nextInput) {
          nextInput.focus();
          // Select all text in the next input for easy replacement
          this.selectInputContent(nextInput);
        }
      } else {
        // Last input filled, trigger completion callback only on first attempt
        if (onComplete && typeof onComplete === 'function' && !this.hasAttemptedSubmission) {
          this.hasAttemptedSubmission = true;
          onComplete(this.getCode());
        }
      }
    } else if (value.length === 0 && event.key === 'Backspace') {
      // Move to previous input on backspace if current is empty
      if (index > 0) {
        const prevInput = input.parentElement.children[index - 1];
        if (prevInput) {
          prevInput.focus();
          // Select all text in the previous input for easy replacement
          this.selectInputContent(prevInput);
        }
      }
    }
  }

  /**
   * Handle input events (including paste operations)
   * @param {number} index - The index of the current input field
   * @param {Event} event - The input event
   * @param {Function} onComplete - Callback when all digits are filled
   */
  handleInput(index, event, onComplete) {
    const input = event.target;
    let value = input.value;

    // Only allow digits
    if (!/^\d*$/.test(value)) {
      value = value.replace(/\D/g, '');
      input.value = value;
    }

    // Handle multi-character input (likely from paste)
    if (value.length > 1) {
      // Extract digits and distribute them across inputs
      const digits = value.replace(/\D/g, '').split('');

      // Get all input elements
      const inputs = this.getInputElements(input);
      if (inputs) {
        // Clear current input first
        input.value = '';
        this.values[index] = '';

        // Fill characters starting from current index
        for (let i = 0; i < digits.length && (index + i) < this.digitCount; i++) {
          const currentIndex = index + i;
          const digit = digits[i];

          if (inputs[currentIndex]) {
            inputs[currentIndex].value = digit;
            this.values[currentIndex] = digit;
          }
        }

        // Focus the appropriate input
        const nextEmptyIndex = this.values.findIndex((val, idx) => val === '' && idx >= index);
        const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : Math.min(index + digits.length, this.digitCount - 1);

        if (inputs[focusIndex]) {
          inputs[focusIndex].focus();
          this.selectInputContent(inputs[focusIndex]);
        }

        // Check for completion after a brief delay, only on first attempt
        setTimeout(() => {
          if (this.isComplete() && onComplete && typeof onComplete === 'function' && !this.hasAttemptedSubmission) {
            this.hasAttemptedSubmission = true;
            onComplete(this.getCode());
          }
        }, 0);
      }
    } else {
      // Single character input - update tracked array
      this.values[index] = value;

      // Check for completion, only on first attempt
      if (this.isComplete() && onComplete && typeof onComplete === 'function' && !this.hasAttemptedSubmission) {
        this.hasAttemptedSubmission = true;
        onComplete(this.getCode());
      }
    }
  }

  /**
   * Handle paste events for code input fields
   * @param {number} index - The index of the current input field
   * @param {Event} event - The paste event
   * @param {Function} onComplete - Callback when all digits are filled
   */
  handlePaste(index, event, onComplete) {
    this.showError = false;

    // Prevent default paste behavior
    event.preventDefault();

    // Get pasted text - handle both clipboardData and fallback methods
    let pastedText = '';
    if (event.clipboardData) {
      pastedText = event.clipboardData.getData('text');
    } else if (window.clipboardData) {
      pastedText = window.clipboardData.getData('text');
    } else {
      // Fallback for right-click paste or other scenarios
      return;
    }

    // Extract only digits from pasted text
    const digits = pastedText.replace(/\D/g, '').split('');

    if (digits.length === 0) {
      return;
    }

    // Get all input elements
    const inputs = this.getInputElements(event.target);
    if (!inputs) {
      return;
    }

    // Fill characters one by one starting from the current index
    let filledCount = 0;
    for (let i = 0; i < digits.length && (index + i) < this.digitCount; i++) {
      const currentIndex = index + i;
      const digit = digits[i];

      // Update the input value
      inputs[currentIndex].value = digit;

      // Update the tracked array
      this.values[currentIndex] = digit;

      filledCount++;
    }

    // Focus the next empty input or the last input if all are filled
    const nextEmptyIndex = this.values.findIndex((value, idx) => value === '' && idx >= index);
    const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : Math.min(index + filledCount, this.digitCount - 1);

    if (inputs[focusIndex]) {
      inputs[focusIndex].focus();
      this.selectInputContent(inputs[focusIndex]);
    }

    // Trigger completion callback if all digits are filled, only on first attempt
    // Use setTimeout to ensure all DOM updates are complete
    setTimeout(() => {
      if (this.isComplete() && onComplete && typeof onComplete === 'function' && !this.hasAttemptedSubmission) {
        this.hasAttemptedSubmission = true;
        onComplete(this.getCode());
      }
    }, 0);
  }

    /**
   * Get all input elements from the current input's parent
   * @param {HTMLElement} currentInput - The current input element
   * @returns {Array|null} Array of input elements or null if not found
   */
  getInputElements(currentInput) {
    if (!currentInput || !currentInput.parentElement) {
      return null;
    }

    // Get all input elements from the parent container
    const inputs = Array.from(currentInput.parentElement.querySelectorAll('input[type="text"], input[type="number"]'));

    if (inputs.length === 0) {
      return null;
    }

    // Sort inputs by their index in the DOM (which should match the template iteration order)
    inputs.sort((a, b) => {
      // Extract index from the id attribute (e.g., "code-input-0", "code-input-1", "reset-code-input-0", etc.)
      const aIndex = parseInt(a.id.replace(/^(code-input-|reset-code-input-)/, ''), 10);
      const bIndex = parseInt(b.id.replace(/^(code-input-|reset-code-input-)/, ''), 10);

      if (isNaN(aIndex) || isNaN(bIndex)) {
        // Fallback to DOM position if id parsing fails
        const aRect = a.getBoundingClientRect();
        const bRect = b.getBoundingClientRect();

        // Sort by top position first, then by left position
        if (Math.abs(aRect.top - bRect.top) < 5) {
          return aRect.left - bRect.left;
        }
        return aRect.top - bRect.top;
      }

      return aIndex - bIndex;
    });

    return inputs;
  }

  /**
   * Get the complete code as a string
   * @returns {string} The complete code
   */
  getCode() {
    return this.values.join('');
  }

  /**
   * Check if all digits are filled
   * @returns {boolean} True if all digits are filled
   */
  isComplete() {
    return this.values.every(value => value !== '');
  }

  /**
   * Clear all input values
   */
  clear() {
    this.values = new Array(this.digitCount).fill('');
    this.showError = false;
    // Note: We don't reset hasAttemptedSubmission here to maintain the "permanently disabled" behavior
  }

  /**
   * Set error state
   * @param {boolean} hasError - Whether to show error state
   */
  setError(hasError) {
    this.showError = hasError;
  }

  /**
   * Get the values array for template iteration
   * @returns {Array} Array of values for template rendering
   */
  getValuesForTemplate() {
    return this.values;
  }

  /**
   * Check if manual submission is required (after first attempt)
   * @returns {boolean} True if manual submission is required
   */
  get requiresManualSubmission() {
    return this.hasAttemptedSubmission && this.isComplete();
  }

  /**
   * Manually trigger submission (for use after first attempt)
   * @param {Function} onComplete - Callback when submission is triggered
   */
  manualSubmit(onComplete) {
    if (this.isComplete() && onComplete && typeof onComplete === 'function') {
      onComplete(this.getCode());
    }
  }

  /**
   * Reset the submission attempt flag (useful when starting a new code entry)
   */
  resetSubmissionAttempt() {
    this.hasAttemptedSubmission = false;
  }

  /**
   * Completely reset the manager (including submission attempt flag)
   * Use this when starting a completely new flow
   */
  reset() {
    this.values = new Array(this.digitCount).fill('');
    this.showError = false;
    this.hasAttemptedSubmission = false;
    this.isNavigatingWithArrows = false;
  }
}